import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container, Typography, Box, CircularProgress, Tabs, Tab,
  Card, CardContent, Switch, FormControlLabel, Divider,
  Button, Alert, Skeleton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import {
  Person, Email, Lock, Save, Warning, ReceiptLong,
} from '@mui/icons-material';

import ClientProfile from '../Profile/ClientProfile';
import ProviderBillingTab from '../../components/Billing/ProviderBillingTab';
import userService from '../../services/userService';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function SettingRow({ label, description, checked, onChange }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 1.5 }}>
      <Box sx={{ flex: 1, mr: 2 }}>
        <Typography variant="body1">{label}</Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        )}
      </Box>
      <Switch checked={!!checked} onChange={onChange} color="primary" />
    </Box>
  );
}

function SectionCard({ title, icon, children }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon && React.cloneElement(icon, { sx: { mr: 1, color: 'primary.main' } })}
          <Typography variant="h6" color="primary.dark">{title}</Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

const EMAIL_LABELS = {
  messageNotification: { label: 'Üzenet értesítő', description: 'Email küldése, ha új üzeneted érkezett' },
  invitation: { label: 'Meghívó értesítők', description: 'Esküvői eseményekre vonatkozó meghívók' },
  bookingApproved: { label: 'Foglalás visszaigazolva', description: 'Email, ha egy foglalásod megerősítésre kerül' },
  bookingRejected: { label: 'Foglalás elutasítva', description: 'Email, ha egy foglalásod elutasításra kerül' },
  bookingCancelled: { label: 'Foglalás lemondva', description: 'Email, ha egy foglalás lemondásra kerül' },
  bookingOfferReceived: { label: 'Ajánlat érkezett', description: 'Email, ha egy szolgáltató ajánlatot küld' },
  bookingOfferAccepted: { label: 'Ajánlat elfogadva', description: 'Email, ha az ajánlatod elfogadásra kerül' },
  bookingOfferRejected: { label: 'Ajánlat visszautasítva', description: 'Email, ha az ajánlatod visszautasításra kerül' },
  bookingNewRequest: { label: 'Új ajánlatkérés', description: 'Email, ha új foglalási kérelem érkezett' },
  bookingNewOfferRequest: { label: 'Új ajánlat kérése', description: 'Email, ha egy ügyfél újabb ajánlatot kér' },
  featuredPurchaseSuccess: { label: 'Előfizetés vásárlás sikeres', description: 'Visszaigazolás sikeres vásárlás után' },
  featuredExpiryWarning: { label: 'Lejárat előtti figyelmeztetés', description: 'Emlékeztető, ha az előfizetésed hamarosan lejár' },
  featuredExpired: { label: 'Előfizetés lejárt', description: 'Értesítés, ha a kiemelt előfizetésed lejárt' },
  featuredRenewalSuccess: { label: 'Automatikus megújítás sikeres', description: 'Visszaigazolás sikeres automatikus megújításról' },
  featuredRenewalFailed: { label: 'Automatikus megújítás sikertelen', description: 'Figyelmeztetés, ha a megújítás sikertelen volt' },
};

const CLIENT_BOOKING_KEYS = [
  'bookingApproved', 'bookingRejected', 'bookingCancelled',
  'bookingOfferReceived', 'bookingOfferAccepted', 'bookingOfferRejected',
];

const PROVIDER_BOOKING_KEYS = [
  'bookingNewRequest', 'bookingNewOfferRequest', 'bookingApproved',
  'bookingCancelled', 'bookingOfferAccepted', 'bookingOfferRejected',
];

const PROVIDER_FEATURED_KEYS = [
  'featuredPurchaseSuccess', 'featuredExpiryWarning', 'featuredExpired',
  'featuredRenewalSuccess', 'featuredRenewalFailed',
];

const PRIVACY_LABELS = {
  showPhoneToProviders: {
    label: 'Telefonszám megjelenítése',
    description: 'A telefonszámod látható lesz a foglalásban részt vevő szolgáltatóknak',
  },
  allowProfileIndexing: {
    label: 'Profil megjelenítése keresési eredményekben',
    description: 'A profilod megjelenhet a platform nyilvános kereső oldalain',
  },
};

const Settings = () => {
  const { user, isProvider, isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = parseInt(searchParams.get('tab') || '0', 10);
  // A 3-as (Számlázás) fül csak szolgáltatónak létezik — az auth betöltése előtt még nem tudjuk
  const maxTabs = 3;
  const [currentTab, setCurrentTab] = useState(Number.isNaN(tabFromUrl) ? 0 : Math.min(tabFromUrl, maxTabs));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [emailNotifications, setEmailNotifications] = useState({});
  const [privacySettings, setPrivacySettings] = useState({});
  const [subscribedToNewsletter, setSubscribedToNewsletter] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleTabChange = (_, newValue) => {
    setCurrentTab(newValue);
    setSearchParams({ tab: newValue });
    setSuccessMsg(null);
    setError(null);
  };

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await userService.getSettings();
      if (res.success) {
        setEmailNotifications(res.data.emailNotifications || {});
        setPrivacySettings(res.data.privacySettings || {});
        setSubscribedToNewsletter(res.data.subscribedToNewsletter || false);
      }
    } catch {
      setError('A beállítások betöltése sikertelen. Kérjük próbálja újra.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadSettings();
  }, [authLoading, isAuthenticated, navigate, loadSettings]);

  const handleSavePreferences = async () => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await userService.updateSettings({
        emailNotifications,
        privacySettings,
        subscribedToNewsletter,
      });
      if (res.success) {
        setSuccessMsg('Beállítások sikeresen mentve.');
        setTimeout(() => setSuccessMsg(null), 3000);
      } else {
        setError('Mentés sikertelen. Kérjük próbálja újra.');
      }
    } catch {
      setError('Mentés sikertelen. Kérjük próbálja újra.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await userService.deleteAccount();
      await logout();
      navigate('/');
    } catch {
      setError('A fiók törlése sikertelen. Kérjük próbálja újra.');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const toggleEmail = (key) =>
    setEmailNotifications((prev) => ({ ...prev, [key]: !prev[key] }));

  const togglePrivacy = (key) =>
    setPrivacySettings((prev) => ({ ...prev, [key]: !prev[key] }));

  const provider = isProvider && isProvider();
  const activeTab = !provider && currentTab === 3 ? 0 : currentTab;

  if (authLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const renderEmailSkeleton = () => (
    <>
      {[1, 2, 3, 4].map((i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton width="60%" />
            <Skeleton width="40%" />
          </Box>
          <Skeleton variant="rectangular" width={50} height={30} sx={{ ml: 2 }} />
        </Box>
      ))}
    </>
  );

  const SaveButton = () => (
    <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Button
        variant="contained"
        startIcon={<Save />}
        onClick={handleSavePreferences}
        disabled={saving}
      >
        {saving ? 'Mentés...' : 'Mentés'}
      </Button>
      {successMsg && <Alert severity="success" sx={{ flex: 1 }}>{successMsg}</Alert>}
      {error && currentTab !== 0 && <Alert severity="error" sx={{ flex: 1 }}>{error}</Alert>}
    </Box>
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Beállítások
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="beállítások fülek"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Person />} iconPosition="start" label="Profil" />
          <Tab icon={<Email />} iconPosition="start" label="Email értesítések" />
          <Tab icon={<Lock />} iconPosition="start" label="Adatvédelem" />
          {provider && <Tab icon={<ReceiptLong />} iconPosition="start" label="Számlázás" />}
        </Tabs>
      </Box>

      {/* TAB 0: PROFIL */}
      <TabPanel value={activeTab} index={0}>
        {provider && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" color="primary.dark">Szolgáltatói profil</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Üzleti adatok, leírás, galéria, kategória és árak szerkesztése.
              </Typography>
              {user?.provider?.id ? (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate('/profil/szolgaltato')}
                >
                  Szolgáltatói profil szerkesztése
                </Button>
              ) : (
                <Alert severity="warning">Még nem hozta létre szolgáltatói profilját.</Alert>
              )}
            </CardContent>
          </Card>
        )}
        <ClientProfile embedded />
      </TabPanel>

      {/* TAB 1: EMAIL ÉRTESÍTÉSEK */}
      <TabPanel value={activeTab} index={1}>
        <SectionCard title="Általános" icon={<Email />}>
          {loading ? renderEmailSkeleton() : (
            <>
              {['messageNotification', 'invitation'].map((key) => (
                <React.Fragment key={key}>
                  <SettingRow
                    label={EMAIL_LABELS[key].label}
                    description={EMAIL_LABELS[key].description}
                    checked={emailNotifications[key]}
                    onChange={() => toggleEmail(key)}
                  />
                  <Divider />
                </React.Fragment>
              ))}
            </>
          )}
        </SectionCard>

        <SectionCard title={provider ? 'Foglalások (szolgáltatói)' : 'Foglalások'} icon={<Email />}>
          {loading ? renderEmailSkeleton() : (
            <>
              {(provider ? PROVIDER_BOOKING_KEYS : CLIENT_BOOKING_KEYS).map((key, i, arr) => (
                <React.Fragment key={key}>
                  <SettingRow
                    label={EMAIL_LABELS[key].label}
                    description={EMAIL_LABELS[key].description}
                    checked={emailNotifications[key]}
                    onChange={() => toggleEmail(key)}
                  />
                  {i < arr.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </>
          )}
        </SectionCard>

        {provider && (
          <SectionCard title="Kiemelt előfizetés" icon={<Email />}>
            {loading ? renderEmailSkeleton() : (
              <>
                {PROVIDER_FEATURED_KEYS.map((key, i, arr) => (
                  <React.Fragment key={key}>
                    <SettingRow
                      label={EMAIL_LABELS[key].label}
                      description={EMAIL_LABELS[key].description}
                      checked={emailNotifications[key]}
                      onChange={() => toggleEmail(key)}
                    />
                    {i < arr.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </>
            )}
          </SectionCard>
        )}

        <SectionCard title="Hírlevél" icon={<Email />}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5 }}>
              <Skeleton width="50%" />
              <Skeleton variant="rectangular" width={50} height={30} />
            </Box>
          ) : (
            <SettingRow
              label="Feliratkozás hírlevélre"
              description="Akciók, újdonságok és esküvői tippek a Wedigo-tól"
              checked={subscribedToNewsletter}
              onChange={() => setSubscribedToNewsletter((v) => !v)}
            />
          )}
        </SectionCard>

        <SaveButton />
      </TabPanel>

      {/* TAB 2: ADATVÉDELEM */}
      <TabPanel value={activeTab} index={2}>
        <SectionCard title="Adatvédelem" icon={<Lock />}>
          {loading ? renderEmailSkeleton() : (
            <>
              {Object.entries(PRIVACY_LABELS)
                .filter(([key]) => provider || key !== 'allowProfileIndexing')
                .map(([key, { label, description }], i, arr) => (
                  <React.Fragment key={key}>
                    <SettingRow
                      label={label}
                      description={description}
                      checked={privacySettings[key]}
                      onChange={() => togglePrivacy(key)}
                    />
                    {i < arr.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
            </>
          )}
        </SectionCard>

        <SaveButton />

        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'error.light' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Warning sx={{ mr: 1, color: 'error.main' }} />
              <Typography variant="h6" color="error.main">Fiók törlése</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A fiókod deaktiválásra kerül és nem fogsz tudni bejelentkezni. A meglévő
              foglalásaid, üzeneteid és értékeléseid megmaradnak a rendszerben. Ha
              meggondolod magad, keresd az ügyfélszolgálatot a fiók visszaállításához.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
            >
              Fiók törlése
            </Button>
          </CardContent>
        </Card>

        <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="error" />
            Biztosan törölni szeretné a fiókját?
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              A fiókod <strong>deaktiválásra kerül</strong>: nem fogsz tudni bejelentkezni, és
              a szolgáltatói profilod (ha van) eltűnik a keresésből. A foglalásaid, üzeneteid
              és értékeléseid <strong>megmaradnak</strong> a rendszerben. A fiók
              visszaállításához az ügyfélszolgálathoz kell fordulnod.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Mégse
            </Button>
            <Button
              onClick={handleDeleteAccount}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting ? 'Törlés...' : 'Igen, törlöm a fiókomat'}
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* TAB 3: SZÁMLÁZÁS (csak szolgáltató) */}
      {provider && (
        <TabPanel value={activeTab} index={3}>
          <ProviderBillingTab />
        </TabPanel>
      )}

    </Container>
  );
};

export default Settings;
